import { reactive, watch } from 'vue'
import { MacroDetails, PostMagnetSenior } from '@libs/types/response'
import { DriverService } from './driver'
import { DriverSocketMsgType, DriverSocketMsg } from '@libs/types/message'
import { enableDialog } from '@/components/cdialog/dialog'
import i18n from '@/languages/i18n'
import { operate } from './operate'
import { KK, VK } from '@/define/define'
import { macro } from './action_set'
import { ResSetData, SetMagnetSenior } from '../response'
import { MagnetSeniorType } from '@libs/types/enum'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import firmware from '@/upgrade/firmware'
export enum MapKeyType {
  base = 1,
  Media = 2,
  Mouse = 3,
  Macro = 4,
  Modifier = 5,
  Magnet = 6,
  Fire = 7,
}

class Data {
  private data = reactive({} as ResSetData)

  private deleteLock = false

  private onDynamicStateChange: ((isRunning: boolean) => void) | null = null

  init() {
    this.askData()
  }

  async askData() {
    try {
      const res: ResSetData = await DriverService.instance.askType<ResSetData>(DriverSocketMsgType.MTReadSettings)
      console.log('read settings data', res)

      if (res) {
        this.data.ColorsDynamic = { ...res.ColorsDynamic }
        this.data.LightLastIsCustom = res.LightLastIsCustom
        this.data.LightCustom = { ...res.LightCustom }
        this.data.LightMode = res.LightMode
        this.data.Mouse = { ...res.Mouse }
        this.data.Layout = { ...res.Layout }
        this.data.LayoutFn = { ...res.LayoutFn }
        this.data.Decorates = res.Decorates ? res.Decorates : []

        this.watchDynamicColor()
      }
    } catch (error) {
      console.error('read settings data error:', error)
    }
  }

  isDKS = (kk: KK): boolean => {
    return this.getData().Layout.Magenet[kk] && this.getData().Layout.Magenet[kk].KeyType == MagnetSeniorType.SeniorKeyTypeDKS
  }

  getData() {
    return this.data
  }

  get getActionData() {
    let IsFn = operate.fnCheck.value
    if (IsFn) {
      return this.data.LayoutFn
    } else {
      return this.data.Layout
    }
  }

  setKeyMap(key: KK, value: VK, KeyModifier: VK[], type: MapKeyType = MapKeyType.base) {
    const modifier = KeyModifier.length > 0 ? { [key]: KeyModifier } : {}
    return this.saveKeyMap({ [key]: value }, type, modifier, {}).then((_) => {
      this.getActionData.KeyMap[key] = value
      if (KeyModifier.length > 0) {
        this.getActionData.KeyMapModifier[key] = KeyModifier.concat([value])
      }
    })
  }

  setKeyFire(key: KK, value: number[]) {
    return this.saveKeyMap({ [key]: 0 }, MapKeyType.Fire, {}, { [key]: value }).then((_) => {
      this.getActionData.KeyMapFire[key] = value
    })
  }

  setKeyMedia(key: KK, value: VK) {
    if (this.deleteLock) {
      return
    }
    return this.saveKeyMap({ [key]: value }, MapKeyType.Media, {}, {}).then((_) => {
      this.getActionData.KeyMap[key] = value
    })
  }

  watchDynamicColor() {
    watch(this.data.ColorsDynamic, async (_value) => {
      await this.saveDynamicColor()
    })
  }
  private setColorTimeout: NodeJS.Timeout | null = null

  private lightUnlock = () => {
    if (this.setColorTimeout) {
      this.clearCustomColorTimeout()
    }
  }

  setKeysColor = async (newValue: { [index: KK]: number }, oldValue: { [index: KK]: number }) => {
    this.data.LightCustom = { ...newValue }
    // 用于取消自定义颜色，如果没有数据，不需要延迟，直接发送数据
    if (Object.keys(this.data.LightCustom).length === 0) {
      this.saveCustomColor()
      return
    }
    this.setCustomColorInterval(oldValue)
  }
  // 获取延迟时间
  private getDelayTime = async () => {
    if (specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)) {
      // 1.06一下的固件，出现写入慢的问题，因此延时要增加
      return (await firmware.isHuoChaiRenV106()) ? 300 : 1200
    }
    return 100
  }

  setCustomColorInterval = async (oldValue: { [index: KK]: number }) => {
    if (this.setColorTimeout) {
      clearTimeout(this.setColorTimeout)
    }
    const delayTimer = await this.getDelayTime()
    this.setColorTimeout = setTimeout(async () => {
      try {
        await this.saveCustomColor()
      } catch (error) {
        this.data.LightCustom = oldValue
      } finally {
        this.lightUnlock()
      }
      this.clearCustomColorTimeout()
    }, delayTimer)
  }

  private clearCustomColorTimeout = () => {
    if (this.setColorTimeout) {
      clearTimeout(this.setColorTimeout)
      this.setColorTimeout = null
    }
  }

  // 自定义颜色的返回数据
  getColorKeys = (newValue: { [index: KK]: number }) => {
    const keys = []
    for (const key in newValue) {
      keys.push({ Key: Number(key), Value: newValue[Number(key)] })
    }
    return { Keys: keys, Light: this.data.LightMode.Light }
  }

  // setCustomLight(key: KK, colorValue: number) {
  //   this.data.LightLastIsCustom = true
  //   this.data.LightCustom[key] = colorValue
  // }

  private async saveCustomColor() {
    const data = { Type: DriverSocketMsgType.MTSetKeysColor, Data: this.getColorKeys(this.data.LightCustom) } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData(data)
  }

  async saveLightMode() {
    this.lightUnlock()
    const data = {
      Type: DriverSocketMsgType.MTSetLed,
      Data: this.data.LightMode,
    } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData(data)
    this.askData()
  }
  async saveLightDecoration(isShowDialog: boolean = false) {
    this.lightUnlock()
    const { t } = i18n.global
    const data = { Type: DriverSocketMsgType.MTSetLightDecoration, Data: this.data.Decorates } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData(data).catch(() => {
      console.log('set decoration failed')
      isShowDialog ? enableDialog(t('setting_failed')) : {}
      return
    })
    this.askData()
  }

  subscribeOnDynamicStateChange(callback: (isRunning: boolean) => void) {
    this.onDynamicStateChange = callback
  }
  private async saveDynamicColor() {
    this.lightUnlock()
    const data = { Type: DriverSocketMsgType.MTMusicalRhythm, Data: this.data.ColorsDynamic } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData<boolean>(data).catch(() => {
      return false
    })
    if (this.onDynamicStateChange) {
      this.onDynamicStateChange(this.data.ColorsDynamic.Run)
    }
  }
  stopDynamicColor() {
    if (this.data.ColorsDynamic.Run) {
      this.data.ColorsDynamic.Run = false
      this.saveDynamicColor()
    }
  }

  private saveKeyMap(values: { [index: KK]: number }, type: MapKeyType, KeyModifier: { [index: KK]: VK[] }, KeyFire: { [index: KK]: number[] }) {
    const data = {
      Type: DriverSocketMsgType.MTSetKey,
      Data: {
        Key: values,
        KeyModifier: KeyModifier,
        KeyFire: KeyFire,
        KeyType: type,
        IsFn: operate.fnCheck.value,
      },
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData(data).then((_) => {
      this.askData()
      const { t } = i18n.global
      enableDialog(t('set_success'))
    })
  }

  saveKeyMacro(key: number, value: macro) {
    let macros: MacroDetails = {
      Key: key,
      CycleTimes: value.CycleTimes,
      CycleType: value.CycleType,
      Records: value.Records,
      IsFn: operate.fnCheck.value,
    }
    const data = {
      Type: DriverSocketMsgType.MTSetMacro,
      Data: macros,
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData(data).then((_) => {
      this.askData()
      const { t } = i18n.global
      enableDialog(t('macro_set_success'))
    })
  }

  saveMagnetSenior(key: number, value: SetMagnetSenior) {
    let data = {
      Type: DriverSocketMsgType.MTMagnetSeniorKey,
      Data: {
        Key: key,
        Value: value,
        IsFn: operate.fnCheck.value,
      },
    } as DriverSocketMsg<PostMagnetSenior>
    return DriverService.instance.askTypeAndData(data).then(() => {
      this.askData()
      const { t } = i18n.global
      enableDialog(t('setting_success'))
    })
  }
}
let setData = new Data()
export { setData }
